/**
 * What each piece of recoverable work *is* (§23).
 *
 * A recovery point holds bytes, a session id, a revision and a timestamp —
 * everything needed to restore the work, and nothing that says which document
 * it belongs to. "복구할 수 있는 파일 1개" with no name is not a recovery
 * offer; it is a puzzle.
 *
 * The labels live here rather than in the {@link FileStore} port on purpose.
 * The port is implemented five times — Node, memory, browser, Electron,
 * Capacitor — and widening it to carry presentation metadata would mean
 * changing native plugins on two platforms that cannot be built or tested from
 * here. So the split is by what must survive:
 *
 *   - the **bytes** live in the store, which is what §23 actually promises;
 *   - the **labels** live in the origin's own storage, which is a nicety.
 *
 * Losing the labels degrades the offer to "이름을 알 수 없는 문서" and the
 * recovery still works. That is the right way round: the part that can be lost
 * is the part whose loss costs nothing.
 *
 * (This storage exists at all because the shells serve the renderer from a
 * real origin rather than `file://`, which has no persistent storage.)
 */

const KEY = 'modu.recovery.index.v1';

/** Kept small so a forgotten entry cannot grow without bound. */
const MAX_ENTRIES = 64;

export interface RecoveryLabel {
  readonly sessionId: string;
  readonly displayName: string;
  readonly formatId: string | null;
  readonly sizeBytes: number;
  /** When this label was last touched, for expiry. */
  readonly updatedAtMs: number;
}

function storage(): Storage | null {
  try {
    // Reading `localStorage` throws outright in some privacy modes, so the
    // access itself has to be guarded, not just its result.
    return window.localStorage;
  } catch {
    return null;
  }
}

function readAll(): RecoveryLabel[] {
  const store = storage();
  if (!store) return [];
  try {
    const raw = store.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Anything that is not a well-formed label is dropped rather than
    // repaired: a half-read entry would produce a confident wrong name.
    return parsed.filter(isLabel);
  } catch {
    return [];
  }
}

function isLabel(value: unknown): value is RecoveryLabel {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v['sessionId'] === 'string'
    && typeof v['displayName'] === 'string'
    && (typeof v['formatId'] === 'string' || v['formatId'] === null)
    && typeof v['sizeBytes'] === 'number'
    && typeof v['updatedAtMs'] === 'number';
}

function writeAll(labels: readonly RecoveryLabel[]): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(KEY, JSON.stringify(labels.slice(0, MAX_ENTRIES)));
  } catch {
    // A full quota is not worth interrupting an edit for. The bytes are
    // already safe in the store; only the label is at stake.
  }
}

/** Record or refresh what a session is editing. */
export function rememberRecoveryLabel(label: Omit<RecoveryLabel, 'updatedAtMs'>, nowMs: number): void {
  const existing = readAll().filter((l) => l.sessionId !== label.sessionId);
  writeAll([{ ...label, updatedAtMs: nowMs }, ...existing]);
}

/** Look up one session's label, or null when it was never recorded. */
export function recoveryLabel(sessionId: string): RecoveryLabel | null {
  return readAll().find((l) => l.sessionId === sessionId) ?? null;
}

/** Forget a session — its work was recovered, saved or discarded. */
export function forgetRecoveryLabel(sessionId: string): void {
  writeAll(readAll().filter((l) => l.sessionId !== sessionId));
}

/**
 * Drop labels for sessions the store no longer holds points for.
 *
 * Called with the ids that still have recovery points, so the index cannot
 * outlive the data it describes — an offer to recover something that is gone
 * is worse than no offer.
 */
export function pruneRecoveryLabels(liveSessionIds: readonly string[]): void {
  const live = new Set(liveSessionIds);
  writeAll(readAll().filter((l) => live.has(l.sessionId)));
}

/** Every label, newest first. */
export function allRecoveryLabels(): RecoveryLabel[] {
  return readAll().sort((a, b) => b.updatedAtMs - a.updatedAtMs);
}
